import React, { useMemo, useState } from "react";
import { Button, Form, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import ExportButtons from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";

const LIMIT = 25;
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const monthName = (m) => moment(m, "YYYY-MM").format("MMM YYYY");

/**
 * Team Income: how much income a leader's whole downline (every level) has earned, per income,
 * per level, per month and per member, with Growth + Recurring on its own, the team's business
 * (all packages and the new business of the period) and payouts (paid net and pending). Only
 * incomes credited now count; removed ones do not.
 */
const TeamIncome = () => {
  const [form, setForm] = useState({ username: "", from: "", to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "username" ? e.target.value.toUpperCase() : e.target.value }));

  const show = async (e) => {
    e.preventDefault();
    setLoading(true);
    const params = new URLSearchParams({ username: form.username.trim() });
    if (form.from) params.set("from", form.from);
    if (form.to) params.set("to", form.to);
    const { data: res, error } = await apiCall("GET", `/team-income?${params.toString()}`);
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load the report.");
    setData(res?.data || null);
    setSearch("");
    setPage(1);
  };

  const incomes = data?.incomes || [];
  const period = data
    ? data.period.from || data.period.to
      ? `${data.period.from ? moment(data.period.from).format("DD MMM YYYY") : "start"} to ${data.period.to ? moment(data.period.to).format("DD MMM YYYY") : "today"}`
      : "all time"
    : "";

  const members = useMemo(() => {
    const text = search.trim().toLowerCase();
    const all = data?.members || [];
    return text ? all.filter((m) => `${m.username} ${m.name} ${m.sponsor}`.toLowerCase().includes(text)) : all;
  }, [data, search]);
  const pages = Math.max(1, Math.ceil(members.length / LIMIT));
  const shown = members.slice((page - 1) * LIMIT, page * LIMIT);

  const incomeCells = (row) => [
    ...incomes.map((i) => <td key={i.key} className="text-end">{inr(row.incomes[i.key])}</td>),
    <td key="gr" className="text-end ps-strong">{inr(row.growth_recurring)}</td>,
  ];
  const incomeHeads = [
    ...incomes.map((i) => <th key={i.key} className="text-end">{i.label}</th>),
    <th key="gr" className="text-end">Growth + Recurring</th>,
  ];
  const payoutCells = (row) => [
    <td key="net" className="text-end">{inr(row.payout.net)}</td>,
    <td key="pending" className="text-end">{inr(row.payout.pending)}</td>,
  ];
  const payoutHeads = [
    <th key="net" className="text-end">Payout paid (net)</th>,
    <th key="pending" className="text-end">Payout pending</th>,
  ];
  // Income columns plus Growth + Recurring
  const incomeCols = incomes.length + 1;

  return (
    <div className="plan-settings activation-requests-page">
      <div className="ps-header">
        <div>
          <h2>Team Income</h2>
          <p>Income earned by a leader&apos;s whole downline (every level), with Growth + Recurring, team business and payouts, per level, month and member. Removed incomes are not counted.</p>
        </div>
        <ExportButtons
          title={data ? `Team Income ${data.leader.username}` : "Team Income"}
          disabled={!data?.members?.length}
          filters={data ? `leader: ${data.leader.username}, period: ${period}${search.trim() ? `, search: ${search.trim()}` : ""}` : ""}
          columns={[
            { header: "Member", value: (m) => m.username },
            { header: "Name", value: (m) => m.name },
            { header: "Level", value: (m) => m.level },
            { header: "Sponsor", value: (m) => m.sponsor },
            { header: "Status", value: (m) => (m.active ? "Active" : "Inactive") },
            { header: "Package", value: (m) => m.package },
            { header: "New business (period)", value: (m) => m.new_business },
            ...incomes.map((i) => ({ header: i.label, value: (m) => m.incomes[i.key] })),
            { header: "Growth + Recurring", value: (m) => m.growth_recurring },
            { header: "Total income", value: (m) => m.total },
            { header: "Payout gross", value: (m) => m.payout.gross },
            { header: "Payout charges", value: (m) => m.payout.charges },
            { header: "Payout paid (net)", value: (m) => m.payout.net },
            { header: "Payout pending", value: (m) => m.payout.pending },
          ]}
          getRows={async () => members}
        />
      </div>

      <div className="ps-panel">
        <Form onSubmit={show} className="ar-filters">
          <div>
            <Form.Label>Leader ID</Form.Label>
            <Form.Control size="sm" value={form.username} onChange={set("username")} placeholder="e.g. AD1234567" required />
          </div>
          <div>
            <Form.Label>From</Form.Label>
            <Form.Control size="sm" type="date" value={form.from} onChange={set("from")} />
          </div>
          <div>
            <Form.Label>To</Form.Label>
            <Form.Control size="sm" type="date" value={form.to} onChange={set("to")} />
          </div>
          <div className="ar-f-reset">
            <Button size="sm" type="submit" className="ar-primary-btn" disabled={loading || !form.username.trim()}>
              {loading ? <Spinner size="sm" /> : "Show report"}
            </Button>
          </div>
        </Form>
        <div className="ps-muted ps-small mt-2">Leave the dates empty for all time. Dates follow the India calendar and apply to income, new business and payouts; team business is all active packages.</div>
      </div>

      {data && (
        <>
          <div className="ps-stats ar-stats">
            <div className="ps-stat ar-stat ar-stat--approved">
              <span>Team income</span>
              <strong>{inr(data.summary.total)}</strong>
              <small>{data.leader.username}{data.leader.name ? ` (${data.leader.name})` : ""} · {period}</small>
            </div>
            <div className="ps-stat ar-stat">
              <span>Team</span>
              <strong>{data.summary.members}</strong>
              <small>{data.summary.active} active · {data.summary.levels} levels</small>
            </div>
            <div className="ps-stat ar-stat">
              <span>Team business</span>
              <strong>{inr(data.summary.package)}</strong>
              <small>all active packages · new in {period}: {inr(data.summary.new_business)}</small>
            </div>
            <div className="ps-stat ar-stat ar-stat--approved">
              <span>Growth + Recurring</span>
              <strong>{inr(data.summary.growth_recurring)}</strong>
              <small>team · {period}</small>
            </div>
            <div className="ps-stat ar-stat">
              <span>Team payout paid (net)</span>
              <strong>{inr(data.summary.payout.net)}</strong>
              <small>gross {inr(data.summary.payout.gross)} · charges {inr(data.summary.payout.charges)} · pending {inr(data.summary.payout.pending)}</small>
            </div>
            <div className="ps-stat ar-stat ar-stat--pending">
              <span>Leader&apos;s own income</span>
              <strong>{inr(data.leader.total)}</strong>
              <small>not in the team income</small>
            </div>
          </div>

          <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
            <h4 className="mb-3">Income by type</h4>
            <div className="ps-table-wrap">
              <Table className="ps-table">
                <thead>
                  <tr>
                    <th></th>
                    {incomeHeads}
                    <th className="text-end">Total</th>
                    {payoutHeads}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="ps-strong">Team ({data.summary.members})</td>
                    {incomeCells(data.summary)}
                    <td className="text-end ps-strong">{inr(data.summary.total)}</td>
                    {payoutCells(data.summary)}
                  </tr>
                  <tr>
                    <td>Leader {data.leader.username}</td>
                    {incomeCells(data.leader)}
                    <td className="text-end">{inr(data.leader.total)}</td>
                    {payoutCells(data.leader)}
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
            <h4 className="mb-3">By level</h4>
            <div className="ps-table-wrap">
              <Table className="ps-table">
                <thead>
                  <tr>
                    <th>Level</th>
                    <th className="text-end">Members</th>
                    <th className="text-end">Active</th>
                    <th className="text-end">Packages</th>
                    <th className="text-end">New business</th>
                    {incomeHeads}
                    <th className="text-end">Total</th>
                    {payoutHeads}
                  </tr>
                </thead>
                <tbody>
                  {data.levels.length === 0 && (
                    <tr><td colSpan={8 + incomeCols} className="ps-empty">No members below this ID.</td></tr>
                  )}
                  {data.levels.map((l) => (
                    <tr key={l.level}>
                      <td>Level {l.level}</td>
                      <td className="text-end">{l.members}</td>
                      <td className="text-end">{l.active}</td>
                      <td className="text-end">{inr(l.package)}</td>
                      <td className="text-end">{inr(l.new_business)}</td>
                      {incomeCells(l)}
                      <td className="text-end ps-strong">{inr(l.total)}</td>
                      {payoutCells(l)}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>

          <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
            <h4 className="mb-3">By month</h4>
            <div className="ps-table-wrap">
              <Table className="ps-table">
                <thead>
                  <tr>
                    <th>Month</th>
                    {incomeHeads}
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.length === 0 && (
                    <tr><td colSpan={2 + incomeCols} className="ps-empty">No team income in this period.</td></tr>
                  )}
                  {data.months.map((m) => (
                    <tr key={m.month}>
                      <td>{monthName(m.month)}</td>
                      {incomeCells(m)}
                      <td className="text-end ps-strong">{inr(m.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>

          <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
            <h4 className="mb-3">By member</h4>
            <div className="ar-filters">
              <div className="ar-f-search">
                <Form.Label>Search</Form.Label>
                <Form.Control size="sm" placeholder="Member, name or sponsor" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
              </div>
            </div>
            <div className="ps-table-wrap">
              <Table className="ps-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th className="text-end">Level</th>
                    <th>Sponsor</th>
                    <th className="text-end">Package</th>
                    <th className="text-end">New business</th>
                    {incomeHeads}
                    <th className="text-end">Total</th>
                    {payoutHeads}
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 && (
                    <tr><td colSpan={8 + incomeCols} className="ps-empty">{search.trim() ? "Nothing matches this search." : "No members below this ID."}</td></tr>
                  )}
                  {shown.map((m) => (
                    <tr key={m.username}>
                      <td>
                        <div className="ps-strong">{m.username}</div>
                        <div className="ps-muted ps-small">{m.name || ""}{m.active ? "" : `${m.name ? " · " : ""}inactive`}</div>
                      </td>
                      <td className="text-end">{m.level}</td>
                      <td className="ps-small">{m.sponsor || "—"}</td>
                      <td className="text-end">{inr(m.package)}</td>
                      <td className="text-end">{inr(m.new_business)}</td>
                      {incomeCells(m)}
                      <td className="text-end ps-strong">{inr(m.total)}</td>
                      {payoutCells(m)}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="ar-pagination">
              <div className="ar-pagination-info">
                Showing <strong>{members.length ? (page - 1) * LIMIT + 1 : 0}–{Math.min(page * LIMIT, members.length)}</strong> of <strong>{members.length}</strong> · highest income first
              </div>
              <div className="ar-pages">
                <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                <span className="ps-muted ps-small">Page {page} of {pages}</span>
                <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TeamIncome;
